import { useMemo } from "react";

import {
  CHINA_REGION_OPTIONS,
  emptyChinaRegion,
  findCityOption,
  findDistrictCode,
  findProvinceOption,
  type ChinaRegionValue,
} from "../utils/chinaAddress";

type Props = {
  value: ChinaRegionValue;
  onChange: (next: ChinaRegionValue) => void;
  className?: string;
  /** 区县是否展示（可选，可不选） */
  allowDistrict?: boolean;
};

const selectCls =
  "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-amber-400 bg-white";

export default function ChinaRegionPicker({ value, onChange, className = "", allowDistrict = true }: Props) {
  const provinceOpt = useMemo(() => findProvinceOption(value.province), [value.province]);
  const cityOptions = provinceOpt?.children ?? [];
  const cityOpt = useMemo(() => findCityOption(provinceOpt, value.city), [provinceOpt, value.city]);
  const districtOptions = allowDistrict ? cityOpt?.children ?? [] : [];

  const setProvince = (code: string) => {
    const opt = CHINA_REGION_OPTIONS.find((p) => p.value === code);
    onChange({
      province: opt?.label ?? "",
      city: "",
      district: "",
      regionCode: "",
    });
  };

  const setCity = (code: string) => {
    const opt = cityOptions.find((c) => c.value === code);
    onChange({
      province: value.province,
      city: opt?.label ?? "",
      district: "",
      regionCode: "",
    });
  };

  const setDistrict = (code: string) => {
    const opt = districtOptions.find((d) => d.value === code);
    onChange({
      province: value.province,
      city: value.city,
      district: opt?.label ?? "",
      regionCode: code,
    });
  };

  const clearAll = () => onChange(emptyChinaRegion());

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select
          className={selectCls}
          value={provinceOpt?.value ?? ""}
          onChange={(e) => setProvince(e.target.value)}
        >
          <option value="">请选择省</option>
          {CHINA_REGION_OPTIONS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          className={selectCls}
          value={cityOpt?.value ?? ""}
          onChange={(e) => setCity(e.target.value)}
          disabled={!provinceOpt}
        >
          <option value="">请选择市</option>
          {cityOptions.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
        {allowDistrict ? (
          <select
            className={selectCls}
            value={value.regionCode && districtOptions.some((d) => d.value === value.regionCode) ? value.regionCode : findDistrictCode(provinceOpt, cityOpt, value.district)}
            onChange={(e) => setDistrict(e.target.value)}
            disabled={!cityOpt}
          >
            <option value="">请选择区县（可选）</option>
            {districtOptions.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={clearAll}>
        清空地区
      </button>
    </div>
  );
}
